import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, TrendingDown, Tag } from 'lucide-react';
import { Categorie, getCategories } from '../lib/services/CategoryService';
import CategoryForm from '../components/CategoryForm';
import CategoryIconBadge from '../components/CategoryIconBadge';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'depense' | 'revenu'>('depense');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Categorie | undefined>(undefined);

  // Charger les catégories depuis Supabase
  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await getCategories();
      
      if (error) {
        setError('Erreur lors du chargement des catégories');
        console.error('Erreur lors du chargement des catégories:', error);
        return;
      }
      
      setCategories(data || []);
    } catch (err) {
      console.error('Exception lors du chargement des catégories:', err);
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Charger les catégories au montage du composant
  useEffect(() => {
    loadCategories();
  }, []);

  // Filtrage et tri alphabétique des catégories selon le type actif
  const filteredCategories = categories
    .filter(cat => cat.type === activeTab)
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }));

  const handleAddCategory = () => {
    setEditingCategory(undefined);
    setIsFormOpen(true);
  };

  const handleEditCategory = (category: Categorie) => {
    setEditingCategory(category);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (_category: Categorie) => {
    await loadCategories(); // Recharger les catégories
    setIsFormOpen(false);
    setEditingCategory(undefined);
    return { success: true };
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingCategory(undefined);
  };

  if (loading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-white mt-4">Chargement des catégories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Catégories</h2>
          <p className="text-dark-400">
            Gérez vos catégories de dépenses et revenus
          </p>
        </div>
        
        <button
          onClick={handleAddCategory}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nouvelle catégorie</span>
        </button>
      </div>

      {/* Onglets */}
      <div className="flex bg-dark-800 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('depense')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'depense' 
              ? 'bg-primary-500 text-white' 
              : 'text-dark-400 hover:text-white'
          }`}
        >
          <TrendingDown className="w-4 h-4" />
          <span>Dépenses</span>
        </button>
        <button
          onClick={() => setActiveTab('revenu')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
            activeTab === 'revenu' 
              ? 'bg-primary-500 text-white' 
              : 'text-dark-400 hover:text-white'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          <span>Revenus</span>
        </button>
      </div>

      {/* Gestion des erreurs */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={loadCategories}
            className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Liste des catégories */}
      <div className="space-y-3">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Tag className="w-8 h-8 text-dark-400" />
            </div>
            <p className="text-white font-medium mb-2">
              Aucune catégorie {activeTab === 'depense' ? 'de dépense' : 'de revenu'}
            </p>
            <p className="text-dark-400">
              Ajoutez votre première catégorie pour commencer
            </p>
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div
              key={category.id}
              className="flex items-center justify-between p-4 bg-dark-800 rounded-lg hover:bg-dark-700 transition-colors cursor-pointer"
              onClick={() => handleEditCategory(category)}
            >
              <div className="flex items-center space-x-4">
                <CategoryIconBadge
                  color={category.couleur}
                  icon={category.icone}
                  size={48}
                />

                {/* Informations de la catégorie */}
                <div className="flex-1">
                  <h3 className="text-white font-medium">{category.nom}</h3>
                  {category.commentaire && (
                    <p className="text-dark-400 text-sm mt-1">{category.commentaire}</p>
                  )}
                </div>
              </div>

              {/* Flèche */}
              <div className="text-dark-400">
                <span className="text-xl">›</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal du formulaire */}
      <CategoryForm
        category={editingCategory}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        title={editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        defaultType={activeTab}
      />
    </div>
  );
};

export default Categories; 